import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { api, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, RotateCcw, RefreshCcw, Bell, Send, CheckCircle } from "lucide-react";
import ImageUpload from "@/admin/ImageUpload";
import { labelWithHelp } from "@/admin/FieldHelp";
import DateListInput from "@/admin/DateListInput";
import MediaUpload from "@/admin/MediaUpload";
import MultiImageUpload from "@/admin/MultiImageUpload";
import MultiPhotoUpload from "@/admin/MultiPhotoUpload";
import RichTextEditor from "@/admin/RichTextEditor";
import { useConfirm } from "@/lib/confirm";

const CONFIG = {
  batches: { title: "Batches", fields: ["name","age_range","description","image","capacity_default","order","active","free_trial_enabled"], blastAction: true },
  terms: { title: "Terms", fields: ["name","start_date","end_date","classes_count","price_per_class","skip_dates","currency","active","order"], rolloverAction: true, sendRolloverAction: true },
  sessions: { title: "Sessions", fields: ["batch_id","batch_name","location_id","location_name","term_id","day","day_order","start_time","end_time","instructor","capacity","registration_count","price_per_class","pay_amount","prorata_enabled","active"] },
  locations: { title: "Locations", fields: ["name","address","map_url","phone","image","order"] },
  team: { title: "Team", fields: ["name","role","category","crew_id","photo","bio","styles","order"] },
  crews: { title: "Competition Crews", fields: ["name","age_range","days","start_time","end_time","location_id","description","crew_intro","group_photo","photos","achievements","order","active"] },
  achievements: { title: "Accolades", fields: ["title","event","crew_id","category","date","description","photo","order"] },
  workshops: { title: "Workshops", fields: ["hosting","external_url","external_label","host_name","name","description","instructor","date","start_time","end_time","location_name","capacity","registration_count","price","pay_amount","status","image","order"], hostingHelp: true },
  gallery: { title: "Gallery", fields: ["image","video_url","order"] },
  blog: { title: "Blog Posts", fields: ["title","slug","excerpt","content","cover","author","category","published","order"] },
  promotions: { title: "Promotions", fields: ["title","description","icon","active","order"] },
  inquiries: { title: "Inquiries", fields: ["name","email","phone","message","source","status","notes"], readOnlyCreate: true },
  registrations: { title: "Class Registrations", endpoint: "registrations", fields: ["full_name","email","phone","status","payment_status","notes"], instructorHiddenFields: ["phone","email"], readOnlyCreate: true },
  "workshop-registrations": { title: "Workshop Registrations", fields: ["full_name","email","phone","status","payment_status","notes"], instructorHiddenFields: ["phone","email"], readOnlyCreate: true },
  "pre-registrations": { title: "Pre-Registrations", fields: ["full_name","email","phone","batch_id","preferred_day","status","notes"], readOnlyCreate: true, notifyAction: true },
  "competition-applications": { title: "Competition Applications", fields: ["division","full_name","age","email","phone","dance_experience","status","notes"], readOnlyCreate: true, divisionFilter: true },
  reels: { title: "Instagram Reels", fields: ["url","caption","order","active"] },
  events: { title: "Events", fields: ["hosting","external_url","external_label","host_name","image","caption","description","date","location","price","pay_amount","currency","capacity","registration_count","link_label","active","order"], hostingHelp: true },
  "hire-options": { title: "Hire Options", fields: ["name","description","order","active"] },
  "hire-inquiries": { title: "Hire Inquiries", fields: ["full_name","email","phone","hire_option_name","event_date","event_location","message","status","notes"], readOnlyCreate: true },
  "free-trial-registrations": { title: "Free Trials", fields: ["full_name","email","phone","batch_name","converted"], readOnlyCreate: true, trialConvertAction: true },
  payments: { title: "Payments", fields: ["customer_name","customer_email","amount","currency","type","payment_status","status"], readOnlyCreate: true, noDelete: true, refundAction: true, payFilters: true },
  settings: { title: "Website Settings", singleton: true, fields: ["site_name","tagline","business_name","business_abn","business_address","business_phone","business_email","payslip_notes","hero_eyebrow","hero_eyebrow_color","hero_headline","hero_headline_color","hero_sub","hero_sub_color","hero_image","hero_logo","hero_logo_shape","logo_light","logo_dark","favicon","marquee_text","stat_1_value","stat_1_label","stat_2_value","stat_2_label","stat_3_value","stat_3_label","stat_4_value","stat_4_label","classes_intro","classes_intro_color","competition_intro","competition_intro_color","why_eyebrow","why_eyebrow_color","why_title","why_title_color","why_features","google_reviews_enabled","google_place_id","google_places_api_key","google_business_url","contact_email","contact_phone","email_from_name","reply_to_email","owner_notification_email","stripe_secret_key","stripe_webhook_secret","bank_account_name","bank_bsb","bank_account_number","instagram","instagram_handle","facebook","reels_handle","reels_title","primary_color","about_story","about_story_color","footer_note","term_default_classes","term_default_price_per_class","currency","stripe_tax_mode","promo_codes_enabled","seo_title","seo_description","seo_og_image","public_site_url","announcement_active","announcement_text","announcement_link_url","announcement_link_label","announcement_starts_at","announcement_ends_at"] },
  pages: { title: "Legal Pages", fields: ["slug","title","content","published","order"] },
};

const EXPORTABLE = new Set(["inquiries","registrations","workshop-registrations","pre-registrations","payments","competition-applications","free-trial-registrations"]);

export default function CrudPage() {
  const { section } = useParams();
  const cfg = CONFIG[section];
  const { user } = useAuth();
  const isInstructor = user?.role === "instructor";
  const visibleFields = useMemo(() => {
    if (!cfg) return [];
    const hidden = isInstructor ? (cfg.instructorHiddenFields || []) : [];
    return cfg.fields.filter(f => !hidden.includes(f));
  }, [cfg, isInstructor]);
  const readOnly = user?.role === "instructor";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [rollover, setRollover] = useState(null);
  const [q, setQ] = useState("");
  const [payFilter, setPayFilter] = useState({ type: "", sess: "", ws: "" });
  const [sessionsList, setSessionsList] = useState([]);
  const [workshopsList, setWorkshopsList] = useState([]);
  const confirm = useConfirm();

  useEffect(() => {
    if (section === "payments") {
      api.get("/admin/sessions").then(r => setSessionsList(r.data)).catch(() => {});
      api.get("/admin/workshops").then(r => setWorkshopsList(r.data)).catch(() => {});
    }
  }, [section]);


  const load = useCallback(async () => {
    if (!cfg) return;
    if (cfg.singleton) {
      // Prefer admin endpoint (returns masked secrets); fall back to public settings.
      let r;
      try { r = await api.get(`/admin/${section}`); }
      catch { r = await api.get(`/${section}`); }
      setItems(r.data && Object.keys(r.data).length ? [r.data] : [{}]);
    } else if (section === "payments") {
      const params = new URLSearchParams();
      if (payFilter.type) params.set("type", payFilter.type);
      if (payFilter.sess) params.set("class_session_id", payFilter.sess);
      if (payFilter.ws) params.set("workshop_id", payFilter.ws);
      const r = await api.get(`/admin/${section}?${params}`);
      setItems(r.data || []);
    } else {
      const r = await api.get(`/admin/${section}`);
      setItems(r.data || []);
    }
  }, [cfg, section, payFilter]);

  useEffect(() => { load(); }, [load]);

  const save = async (item) => {
    try {
      if (cfg.singleton) {
        await api.put(`/admin/${section}`, item);
      } else if (item.id) {
        await api.put(`/admin/${section}/${item.id}`, item);
      } else {
        await api.post(`/admin/${section}`, item);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const del = async (id) => {
    const ok = await confirm({ title: "Delete this item?", description: "This can't be undone.", danger: true, confirmLabel: "Delete" });
    if (!ok) return;
    try { await api.delete(`/admin/${section}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const [division, setDivision] = useState("");
  const filtered = useMemo(() => items
    .filter(i => !division || i.division === division)
    .filter(i => !q || JSON.stringify(i).toLowerCase().includes(q.toLowerCase())), [items, q, division]);

  if (!cfg) return <div className="p-4 sm:p-8">Unknown section: {section}</div>;

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">{cfg.title}</div>
          <h1 className="font-display text-5xl uppercase">{cfg.title}</h1>
        </div>
        <div className="flex gap-2">
          {!cfg.singleton && <input placeholder="Search…" value={q} onChange={e=>setQ(e.target.value)} className="bg-black border border-white/10 px-4 py-2 text-sm" data-testid="search-input" />}
          {cfg.divisionFilter && (
            <select value={division} onChange={e => setDivision(e.target.value)} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid="division-filter">
              <option value="">All crews ({items.length})</option>
              {[...new Map(items.filter(i => i.division).map(i => [i.division, i.division_label || i.division])).entries()].map(([v, l]) => (
                <option key={v} value={v}>{l} ({items.filter(i => i.division === v).length})</option>
              ))}
            </select>
          )}
          {cfg.payFilters && (
            <>
              <select value={payFilter.type} onChange={e => setPayFilter(p => ({...p, type: e.target.value, sess: "", ws: ""}))} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid="pay-filter-type">
                <option value="">All types</option><option value="class">Class</option><option value="workshop">Workshop</option>
              </select>
              {payFilter.type === "class" && (
                <select value={payFilter.sess} onChange={e => setPayFilter(p => ({...p, sess: e.target.value}))} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid="pay-filter-session">
                  <option value="">All classes</option>
                  {sessionsList.map(s => <option key={s.id} value={s.id}>{s.batch_name} · {s.day} {s.start_time}</option>)}
                </select>
              )}
              {payFilter.type === "workshop" && (
                <select value={payFilter.ws} onChange={e => setPayFilter(p => ({...p, ws: e.target.value}))} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid="pay-filter-workshop">
                  <option value="">All workshops</option>
                  {workshopsList.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              )}
            </>
          )}
          {EXPORTABLE.has(section) && (
            <button onClick={async () => {
              try {
                const r = await api.get(`/admin/export/${section}.csv`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a"); a.href = url; a.download = `${section}-${new Date().toISOString().slice(0,10)}.csv`;
                document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
              } catch { toast.error("Export failed"); }
            }} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="export-csv">Export CSV</button>
          )}
          {!cfg.singleton && !cfg.readOnlyCreate && !readOnly && (
            <button onClick={() => setEditing({})} className="btn-red !py-2 !px-3 !text-xs" data-testid="add-item"><Plus size={14}/>Add</button>
          )}
          {cfg.singleton && !readOnly && (
            <button onClick={() => setEditing(items[0] || {})} className="btn-red !py-2 !px-3 !text-xs" data-testid="edit-settings"><Pencil size={14}/>Edit</button>
          )}
        </div>
      </div>

      {cfg.singleton && items[0] && (
        <div className="card-surface p-6 grid md:grid-cols-2 gap-4">
          {cfg.fields.map(f => (
            <div key={f}><div className="eyebrow mb-1 flex items-center">{labelWithHelp(f)}</div><div className="text-zinc-300 text-sm break-all">{String(items[0][f] ?? "—")}</div></div>
          ))}
        </div>
      )}

      {!cfg.singleton && (
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-xs">
                {visibleFields.slice(0,5).map(f => <th key={f} className="text-left px-4 py-3">{f.replace(/_/g," ")}</th>)}
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item.id || item.session_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  {visibleFields.slice(0,5).map(f => (
                    <td key={f} className="px-4 py-3 max-w-xs truncate">
                      {f === "days" ? ((Array.isArray(item.days) && item.days.length ? item.days : item.day ? [item.day] : []).map(d => d.slice(0, 3)).join(", ") || "—") :
                       f === "division" ? (item.division_label || ({ juniors_teens: "Juniors & Teens (8–17)", adults: "Adults (18+)" })[item.division] || "—") :
                       f === "crew_id" ? (item.crew_name || "—") :
                       typeof item[f] === "boolean" ? (item[f] ? "✓" : "—") :
                       (typeof item[f] === "string" && item[f].startsWith("http")) ? <img src={item[f]} className="h-10 w-10 object-cover" alt=""/> :
                       String(item[f] ?? "—")}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {cfg.rolloverAction && !readOnly && (
                      <button onClick={() => setRollover(item)} className="text-zinc-400 hover:text-red-500 p-1 mr-1" title="Rollover to new term" data-testid={`rollover-${item.id}`}><RotateCcw size={14}/></button>
                    )}
                    {cfg.sendRolloverAction && !readOnly && (
                      <button onClick={async () => {
                        const ok = await confirm({ title: "Send re-enrol emails?", description: `Every paid family will get a one-click link to re-book their spot for ${item.name}. They'll only get one email per next term.`, confirmLabel: "Send emails" });
                        if (!ok) return;
                        try { const r = await api.post(`/admin/terms/${item.id}/send-rollover`);
                          toast.success(`Sent ${r.data.sent} of ${r.data.candidates} re-enrol emails`); }
                        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                      }} className="text-zinc-400 hover:text-emerald-500 p-1 mr-1" title="Send re-enrol emails" data-testid={`send-rollover-${item.id}`}><Send size={14}/></button>
                    )}
                    {cfg.blastAction && (
                      <button onClick={async () => {
                        const ok = await confirm({ title: "Blast waitlist?", description: `Send email + SMS (if configured) to every pre-registered family for '${item.name}'.`, confirmLabel: "Send blast" });
                        if (!ok) return;
                        try { const r = await api.post(`/admin/batches/${item.id}/notify-waitlist`);
                          toast.success(`Blast sent — ${r.data.total_contacted} contacted (${r.data.emails_sent} emails, ${r.data.sms_sent} SMS)`); }
                        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                      }} className="text-zinc-400 hover:text-emerald-500 p-1 mr-1" title="Blast waitlist" data-testid={`blast-${item.id}`}><Send size={14}/></button>
                    )}
                    {cfg.trialConvertAction && !item.converted && (
                      <>
                        <button onClick={async () => {
                          const ok = await confirm({ title: "Send booking link?", description: `Email ${item.email} a personalized 'book the term' link.`, confirmLabel: "Send email" });
                          if (!ok) return;
                          try { await api.post(`/admin/free-trial-registrations/${item.id}/send-conversion`); toast.success("Link sent"); load(); }
                          catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                        }} className="text-zinc-400 hover:text-red-500 p-1 mr-1" title="Send booking link" data-testid={`convert-send-${item.id}`}><Send size={14}/></button>
                        <button onClick={async () => {
                          try { await api.post(`/admin/free-trial-registrations/${item.id}/mark-converted`); toast.success("Marked converted"); load(); }
                          catch { toast.error("Failed"); }
                        }} className="text-zinc-400 hover:text-emerald-500 p-1 mr-1" title="Mark converted" data-testid={`convert-mark-${item.id}`}><CheckCircle size={14}/></button>
                      </>
                    )}
                    {cfg.refundAction && item.payment_status === "paid" && (
                      <button onClick={async () => {
                        const ok = await confirm({ title: "Refund payment?", description: `Refund $${item.amount} to ${item.customer_email}? This releases the spot.`, danger: true, confirmLabel: "Refund" });
                        if (!ok) return;
                        try { await api.post(`/admin/payments/${item.session_id}/refund`); toast.success("Refunded"); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
                      }} className="text-zinc-400 hover:text-red-500 p-1 mr-1" title="Refund" data-testid={`refund-${item.session_id}`}><RefreshCcw size={14}/></button>
                    )}
                    {cfg.notifyAction && !item.waitlist_notified && (
                      <button onClick={async () => {
                        try { const { data } = await api.post(`/admin/pre-registrations/${item.id}/notify`); toast.success("Waitlist alert sent"); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || "No open session"); }
                      }} className="text-zinc-400 hover:text-emerald-500 p-1 mr-1" title="Send waitlist alert" data-testid={`notify-${item.id}`}><Bell size={14}/></button>
                    )}
                    {!cfg.readOnlyCreate && !readOnly && <button onClick={() => setEditing(item)} className="text-zinc-400 hover:text-white p-1" data-testid={`edit-${item.id}`}><Pencil size={14}/></button>}
                    {cfg.readOnlyCreate && <button onClick={() => setEditing(item)} className="text-zinc-400 hover:text-white p-1" data-testid={`view-${item.id}`}><Pencil size={14}/></button>}
                    {!cfg.noDelete && !readOnly && <button onClick={() => del(item.id)} className="text-zinc-400 hover:text-red-500 p-1 ml-2" data-testid={`delete-${item.id}`}><Trash2 size={14}/></button>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={visibleFields.slice(0,5).length + 1} className="text-center py-16 text-zinc-500">No items yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal item={editing} fields={visibleFields} onClose={() => setEditing(null)} onSave={save} title={cfg.title} readOnly={readOnly} resource={section} />
      )}
      {rollover && (
        <RolloverModal term={rollover} onClose={() => setRollover(null)} onDone={() => { setRollover(null); load(); }} />
      )}
    </div>
  );
}

function RolloverModal({ term, onClose, onDone }) {
  const [form, setForm] = useState({
    name: `${term.name} → Next Term`,
    start_date: "", end_date: "",
    classes_count: term.classes_count, price: term.price,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/terms/rollover", {
        source_term_id: term.id,
        new_term: { ...form, classes_count: Number(form.classes_count), price: Number(form.price) },
        copy_registrations: false,
      });
      toast.success(`Cloned ${data.sessions_cloned} sessions into ${data.new_term_name}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Rollover failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6" onClick={onClose}>
      <div className="card-surface p-8 max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-3xl uppercase">Roll over</h2>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <p className="text-zinc-400 text-sm mb-4">Clones all sessions from <strong>{term.name}</strong> into a new term with 0 registrations. Perfect for opening a fresh intake.</p>
        <div className="grid grid-cols-2 gap-3">
          {[["name","New term name"],["start_date","Start date (YYYY-MM-DD)"],["end_date","End date (YYYY-MM-DD)"],["classes_count","Classes count"],["price_per_class","Price per class ($)"]].map(([k,l]) => (
            <div key={k} className={k === "name" ? "col-span-2" : ""}>
              <label className="eyebrow block mb-1">{l}</label>
              <input value={form[k] ?? ""} onChange={e => setForm({...form,[k]:e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid={`rollover-${k}`} />
            </div>
          ))}
        </div>
        <button onClick={submit} disabled={saving} className="btn-red w-full mt-6" data-testid="submit-rollover">
          {saving ? "Cloning…" : "Create new term & clone sessions"}
        </button>
      </div>
    </div>
  );
}

function EditModal({ item, fields, onClose, onSave, title, readOnly, resource }) {
  const [form, setForm] = useState({ ...item });
  const [crews, setCrews] = useState([]);
  const [locations, setLocations] = useState([]);
  useEffect(() => { if (fields.includes("crew_id")) api.get("/crews").then(r => setCrews(r.data || [])).catch(() => {}); }, [fields]);
  useEffect(() => { if (resource === "crews") api.get("/locations").then(r => setLocations(r.data || [])).catch(() => {}); }, [resource]);
  const isBool = (k) => ["active","published","featured","audition_only","free_trial_enabled","announcement_active","prorata_enabled","promo_codes_enabled"].includes(k);
  const isLong = (k) => ["description","excerpt","bio","review","notes","message","about_story","hero_sub","achievements","business_address","payslip_notes","why_features"].includes(k);
  const isRich = (k) => ["content","classes_intro","competition_intro","story","about_story","crew_intro"].includes(k);
  const isMultiImage = (k) => ["photos"].includes(k);
  const isNum = (k) => ["price","pay_amount","classes_per_term","capacity","capacity_default","registration_count","order","day_order","rating","age","term_default_classes","term_default_price_per_class","price_per_class","value","max_uses","uses"].includes(k);  const isImage = (k) => ["image","photo","cover","hero_image","hero_logo","banner_image","group_photo","logo_light","logo_dark","favicon","seo_og_image","poster"].includes(k);
  const isMedia = (k) => ["video_url"].includes(k);
  const isColor = (k) => k === "primary_color" || (typeof k === "string" && k.endsWith("_color"));
  const isDate = (k) => ["start_date","end_date","event_date","announcement_starts_at","announcement_ends_at","term_start","term_end","starts_at","ends_at"].includes(k);
  const isSecret = (k) => ["stripe_secret_key","stripe_webhook_secret"].includes(k);
  const isSelect = (k) => {
    // Resource-scoped selects: category means different things per collection
    if (k === "category") {
      if (resource === "achievements") return ["team","solo","duo","trio","crew","event","school","community"];
      return ["management","instructor","competition","classes","workshops","competitions","performances","students","events","behind_the_scenes"];
    }
    return ({
      stripe_tax_mode: ["diy","calc_only"],
      hosting: ["internal","external"],
      discount_type: ["percent","fixed"],
      applies_to: ["all","class","workshop","event"],
      hero_logo_shape: ["square","rectangle"],
      status: ["new","contacted","follow-up","interested","registered","not_interested","closed","pending","approved","rejected","hidden","pending_payment","confirmed","upcoming","open","almost_full","sold_out","completed","cancelled","under_review","audition_required","audition_scheduled","selected","not_selected","recruited"],
    })[k];
  };

  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 overflow-y-auto" onClick={onClose}>
      <div className={"card-surface p-8 max-w-2xl w-full my-8" + (readOnly ? " pointer-events-auto" : "")} onClick={e=>e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-3xl uppercase">{readOnly ? "View" : item.id ? "Edit" : "New"} {title}</h2>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <fieldset disabled={readOnly} className={readOnly ? "opacity-90" : ""}>
        <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-2">
          {fields.map(f => {
            const options = isSelect(f);
            const external = form.hosting === "external";
            if (["external_url","external_label","host_name"].includes(f) && !external) return null;
            if (f === "hosting") return (
              <div key={f} className="col-span-2 border border-white/10 bg-black/40 p-4" data-testid="hosting-block">
                <label className="eyebrow block mb-1">Who runs it?</label>
                <select value={form.hosting || "internal"} onChange={e => set("hosting", e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="hosting-select">
                  <option value="internal">Hosted by us — visitors register (and pay) on our site</option>
                  <option value="external">Someone else's — send visitors to their event page</option>
                </select>
                <p className="text-zinc-500 text-xs mt-2">
                  {external
                    ? "Visitors see the photo, details and a button that opens the host's page. No registration or payment is taken here."
                    : "Visitors register on our page. If the price is above $0 they pay via Stripe; at $0 it's a free RSVP."}
                </p>
              </div>
            );
            return (
              <div key={f} className={(isLong(f) || isImage(f) || isMedia(f) || isMultiImage(f) || isRich(f)) ? "col-span-2" : ""}>
                {resource === "crews" && f === "location_id" ? (
                  <>
                    <label className="eyebrow block mb-1 flex items-center">{labelWithHelp("location_id", "Training location")}</label>
                    <select value={form.location_id || ""} onChange={e => set("location_id", e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="input-location_id">
                      <option value="">— Not set —</option>
                      {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </>
                ) : resource === "crews" && f === "days" ? (
                  <>
                    <label className="eyebrow block mb-1 flex items-center">{labelWithHelp("days", "Training days")}</label>
                    <div className="flex flex-wrap gap-2" data-testid="input-days">
                      {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d => {
                        const cur = Array.isArray(form.days) && form.days.length ? form.days : (form.day ? [form.day] : []);
                        const on = cur.includes(d);
                        return (
                          <button type="button" key={d} onClick={() => { const next = on ? cur.filter(x => x !== d) : [...cur, d]; const order = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"]; next.sort((a, b) => order.indexOf(a) - order.indexOf(b)); setForm(fm => ({ ...fm, days: next, day: next[0] || "" })); }}
                            className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${on ? "border-red-600 bg-red-600/15 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`} data-testid={`day-toggle-${d.toLowerCase()}`}>{d.slice(0, 3)}</button>
                        );
                      })}
                    </div>
                  </>
                ) : resource === "crews" && (f === "start_time" || f === "end_time") ? (
                  <>
                    <label className="eyebrow block mb-1 flex items-center">{labelWithHelp(f)}</label>
                    <input type="time" value={form[f] || ""} onChange={e => set(f, e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid={`input-${f}`} />
                  </>
                ) : f === "crew_id" ? (
                  <>
                    <label className="eyebrow block mb-1 flex items-center">{labelWithHelp("crew_id", "Crew")}</label>
                    <select value={form.crew_id || ""} onChange={e => { const c = crews.find(x => x.id === e.target.value); setForm({ ...form, crew_id: e.target.value || null, crew_name: c?.name || "" }); }} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="input-crew_id">
                      <option value="">— Not linked to a crew —</option>
                      {crews.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </>
                ) : f === "skip_dates" ? (
                  <DateListInput value={Array.isArray(form[f]) ? form[f] : []} onChange={v => set(f, v)} label="Public holidays / no-class dates" testId="skip-dates" allowWeek />
                ) : isMultiImage(f) ? (
                  <MultiPhotoUpload label={labelWithHelp(f)} value={form[f]} onChange={v => set(f, v)} testId={f}/>
                ) : isMedia(f) ? (
                  <MediaUpload label={labelWithHelp(f)} value={form[f]} onChange={v => set(f, v)} onPoster={v => set("poster", v)} testId={f}/>
                ) : isImage(f) ? (
                  <ImageUpload label={labelWithHelp(f)} value={form[f]} onChange={v => set(f, v)} testId={f}/>
                ) : isRich(f) ? (
                  <>
                    <label className="eyebrow block mb-1 flex items-center">{labelWithHelp(f)}</label>
                    <RichTextEditor value={form[f] || ""} onChange={v => set(f, v)}/>
                  </>
                ) : (
                  <>
                    <label className="eyebrow block mb-1 flex items-center">{labelWithHelp(f)}</label>
                    {isBool(f) ? (
                      <select value={form[f] ? "true" : "false"} onChange={e => set(f, e.target.value === "true")} className="w-full bg-black border border-white/10 px-3 py-2 text-sm">
                        <option value="true">Yes</option><option value="false">No</option>
                      </select>
                    ) : options ? (
                      <select value={form[f] || ""} onChange={e => set(f, e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm">
                        <option value="">—</option>
                        {options.map(o => <option key={o} value={o}>{o.replace(/_/g," ")}</option>)}
                      </select>
                    ) : isLong(f) ? (
                      <>
                        <textarea value={form[f] || ""} onChange={e => set(f, e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" rows={f === "why_features" ? 8 : 3} placeholder={f === "why_features" ? "One card per line — format: Title | Short description\nExample:\nBuild confidence | Shy beginners blossom into expressive performers.\nReal friendships | Local kids and families who become a second crew." : ""} />
                        {f === "why_features" && <div className="text-zinc-500 text-[11px] mt-1">Format: <span className="font-mono">Title | Description</span> — one card per line. Blank lines are ignored.</div>}
                      </>
                    ) : isColor(f) ? (
                      <div className="flex items-center gap-3">
                        <input type="color" value={form[f] || (f === "primary_color" ? "#DC2626" : "#FFFFFF")} onChange={e => set(f, e.target.value)} className="w-14 h-10 bg-black border border-white/10 cursor-pointer" data-testid={f === "primary_color" ? "primary-color-picker" : `color-${f}`}/>
                        <input type="text" value={form[f] || ""} placeholder={f === "primary_color" ? "#DC2626" : "leave blank for default"} onChange={e => set(f, e.target.value)} className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm font-mono focus:border-red-600 outline-none" />
                        {form[f] && f !== "primary_color" && (
                          <button type="button" onClick={() => set(f, "")} className="text-zinc-500 hover:text-red-500 text-xs" title="Reset to default">Reset</button>
                        )}
                        <span className="text-zinc-500 text-xs">Live preview after save</span>
                      </div>
                    ) : isDate(f) ? (
                      <input type={f.endsWith("_at") ? "datetime-local" : "date"} value={(form[f] || "").slice(0, f.endsWith("_at") ? 16 : 10)} onChange={e => set(f, e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid={`date-${f}`} />
                    ) : isSecret(f) ? (
                      <div>
                        <input type="password" value={form[f] ?? ""} placeholder={form[f] ? "•••• stored — replace to update" : "sk_live_… or sk_test_…"} onChange={e => set(f, e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm font-mono focus:border-red-600 outline-none" data-testid={`secret-${f}`} />
                        <div className="text-zinc-500 text-[11px] mt-1">Currently: <span className="font-mono">{form[f] || "not set"}</span>. Type a new key to replace. Leave blank to keep the stored one.</div>
                      </div>
                    ) : (
                      <>
                      <input type={isNum(f) ? "number" : "text"} value={form[f] ?? ""} onChange={e => set(f, isNum(f) ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" placeholder={f === "instagram" ? "instagram.com/2rightfeet · or just @2rightfeet" : f === "facebook" ? "facebook.com/2rightfeet · or just the page slug" : f === "instagram_handle" || f === "reels_handle" ? "@2rightfeet" : f === "reels_title" ? "Latest reels." : ""} />
                      {(f === "instagram" || f === "facebook") && form[f] && (
                        <div className="text-zinc-500 text-[11px] mt-1">Preview link: <a href={f === "instagram" ? `https://instagram.com/${String(form[f]).replace(/^https?:\/\/(www\.)?instagram\.com\//i,'').replace(/^@/,'')}` : `https://facebook.com/${String(form[f]).replace(/^https?:\/\/(www\.)?facebook\.com\//i,'').replace(/^@/,'')}`} target="_blank" rel="noreferrer" className="text-red-500 underline">{f === "instagram" ? `https://instagram.com/${String(form[f]).replace(/^https?:\/\/(www\.)?instagram\.com\//i,'').replace(/^@/,'')}` : `https://facebook.com/${String(form[f]).replace(/^https?:\/\/(www\.)?facebook\.com\//i,'').replace(/^@/,'')}`}</a></div>
                      )}
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
        </fieldset>
        <div className="flex gap-2 mt-6">
          {!readOnly && <button onClick={() => onSave(form)} className="btn-red flex-1" data-testid="save-item">Save</button>}
          <button onClick={onClose} className="btn-ghost flex-1">{readOnly ? "Close" : "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}
